import cache from '../cache/cache';
import { InterpolationType } from '../enums';
import { mat3, vec3 } from 'gl-matrix';
import type {
  BoundsIJK,
  Mat3,
  Point3,
  PixelDataTypedArray,
  IImage,
  RGB,
  CPUImageData,
  IVolumeVoxelManager,
  IVoxelManager,
  IRLEVoxelMap,
  Point2,
  PixelDataTypedArrayString,
} from '../types';
import { getConstructorFromType } from './getBufferConfiguration';
import RLEVoxelMap from './RLEVoxelMap';
import isEqual from './isEqual';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { iterateOverPointsInShapeVoxelManager } from './pointInShapeCallback';
import { coreLog } from './logger';

const log = coreLog.getLogger('utilities', 'VoxelManager');

/**
 * Have a default size for cached RLE encoded images.  This is hard to guess
 * up front because the RLE is usually used to store new/updated data, but this
 * is a first guess.
 */
export const DEFAULT_RLE_SIZE = 5 * 1024;
const worldToIndexDeltaScratch = [0, 0, 0] as vec3;
const worldToIndexResultScratch = [0, 0, 0] as vec3;
const worldToIndexTransformCache = new WeakMap<object, mat3>();

type VoxelVolumeGeometry = {
  origin: Point3;
  spacing: Point3;
  direction: Mat3;
};

type SampleableVoxelVolume = VoxelVolumeGeometry & {
  dimensions: Point3;
  voxelManager?: IVoxelManager<number> | IVoxelManager<RGB>;
};

/**
 * The lookup that takes a column and a row of a slice of a volume to an index
 * in an image that holds a different number of voxels in the plane.
 *
 * `columns` gives the column of the image, and `rows` gives the start of the
 * row of the image, so the sum of the two is the index of the voxel.
 */
type SliceScaling = {
  columns: Int32Array;
  rows: Int32Array;
};

/**
 * This is a simple, standard interface to values associated with a voxel.
 *
 * The class declares `implements IVoxelManager<T>`, so the compiler checks that
 * the class and the interface stay in agreement. `IVoxelManager` is the
 * structural interface, and a composite voxel manager can implement it without
 * a subclass of this class.
 */
export default class VoxelManager<T> implements IVoxelManager<T> {
  public modifiedSlices = new Set<number>();
  private boundsIJK = [
    [Infinity, -Infinity],
    [Infinity, -Infinity],
    [Infinity, -Infinity],
  ] as BoundsIJK;

  public map: Map<number, T> | IRLEVoxelMap<T>;
  public sourceVoxelManager: IVoxelManager<T>;
  public isInObject: (pointLPS, pointIJK) => boolean;
  public readonly dimensions: Point3;
  public readonly numberOfComponents: number;
  // These four members are not methods. A factory installs each one on the
  // instance after the constructor returns, and only some of the factories
  // install them: `createScalarVolumeVoxelManager` and
  // `createImageVoxelManager` install none of the four. Every one of the four
  // is therefore OPTIONAL. `getRange` carried no `?` before, which promised a
  // caller that the call was always safe while `getRange()` threw a TypeError
  // on the instances of those two factories.
  public getCompleteScalarDataArray?: () => ArrayLike<number>;
  public setCompleteScalarDataArray?: (scalarData: ArrayLike<number>) => void;
  public invalidateCache?: () => void;
  /**
   * Forgets what this voxel manager holds about one slice, so the next read of
   * that slice resolves it again.
   */
  public invalidateSlice?: (sliceIndex: number) => void;
  public getRange?: () => [number, number];
  private scalarData = null as PixelDataTypedArray;
  // True only when `scalarData` is a cached expansion produced by
  // `getScalarData(true)` from `_getScalarData` (e.g. an RLE decode), as opposed
  // to a real backing store. A cached expansion is a snapshot that goes stale
  // after a voxel write, so the setters invalidate it; a backing store must
  // never be invalidated. See `getScalarData` / `invalidateCachedScalarExpansion`.
  private _scalarDataIsCachedExpansion = false;
  // caching for sliceData as it is expensive to get it from the cache
  // I think we need to have a way to invalidate this cache and also have
  // a limit on the number of slices to cache since it can grow indefinitely
  private _sliceDataCache = null as Map<string, PixelDataTypedArray>;

  // The backing field of the `id` getter, and nothing else reads it, so it is
  // private and it stays out of `IVoxelManager`. An implementer of the
  // interface supplies `id` in whatever way suits it.
  private readonly _id: string;

  points: Set<number>;
  width: number;
  frameSize: number;
  readonly _get: (index: number) => T;
  readonly _set: (index: number, v: T) => boolean;
  readonly _getConstructor?: () => new (length: number) => PixelDataTypedArray;
  _getScalarDataLength?: () => number;
  _getScalarData?: () => ArrayLike<number>;
  _updateScalarData?: (scalarData: ArrayLike<number>) => PixelDataTypedArray;
  /**
   * A shorter path to one slice, for a voxel manager that already holds the
   * values of that slice together.
   *
   * `getSliceData` calls this member first, and it composes the slice voxel by
   * voxel only when this member gives nothing back.
   */
  _getSliceData?: (args: {
    sliceIndex: number;
    slicePlane: number;
  }) => PixelDataTypedArray | undefined;
  /**
   * The values of one k slice as the voxel manager already holds them, with no
   * copy, or nothing when it does not hold them together. Read only.
   */
  _getSliceView?: (sliceIndex: number) => PixelDataTypedArray | undefined;

  /**
   * Gets the ID of the voxel manager
   */
  public get id(): string {
    return this._id;
  }

  /**
   * Creates a generic voxel value accessor, with access to the values
   * provided by the _get and optionally _set values.
   * @param dimensions - for the voxel volume
   * @param options._get - called to get a value by index
   * @param options._set  - called when setting a value
   */
  constructor(
    dimensions,
    options: {
      _get: (index: number) => T;
      _set?: (index: number, v: T) => boolean;
      _getScalarData?: () => ArrayLike<number>;
      _id?: string;
      _updateScalarData?: (
        scalarData: ArrayLike<number>
      ) => PixelDataTypedArray;
      numberOfComponents?: number;
      scalarData?: ArrayLike<number>;
      _getConstructor?: () => new (length: number) => PixelDataTypedArray;
    }
  ) {
    this.dimensions = dimensions;
    this.width = dimensions[0];
    this.frameSize = this.width * dimensions[1];
    this._get = options._get;
    this._set = options._set;
    this._id = options._id || '';
    this._getConstructor = options._getConstructor;
    this.numberOfComponents = options.numberOfComponents || 1;
    this.scalarData = options.scalarData as PixelDataTypedArray;
    this._getScalarData = options._getScalarData;
    this._updateScalarData = options._updateScalarData;
  }

  /**
   * Gets the voxel value at position i,j,k.
   * This method should be used in favor of getAtIJKPoint when performance is a concern.
   */
  public getAtIJK = (i, j, k) => {
    return this._get(i + j * this.width + k * this.frameSize);
  };

  /**
   * Sets the voxel value at position i,j,k and records the slice
   * that was modified.
   *
   * This method should be used in favor of setAtIJKPoint when performance is a concern.
   */
  public setAtIJK = (i: number, j: number, k: number, v) => {
    const index = i + j * this.width + k * this.frameSize;
    const changed = this._set(index, v);
    if (changed !== false) {
      this.modifiedSlices.add(k);
      VoxelManager.addBounds(this.boundsIJK, [i, j, k]);
      this.invalidateCachedScalarExpansion();
    }

    return changed;
  };

  /**
   * Gets the voxel value at the given Point3 location.
   */
  public getAtIJKPoint = ([i, j, k]) => this.getAtIJK(i, j, k);

  /**
   * Sets the voxel value at the given point3 location to the specified value.
   * Records the z index modified.
   * Will record the index value if the VoxelManager is backed by a map.
   */
  public setAtIJKPoint = ([i, j, k]: Point3, v) => {
    this.setAtIJK(i, j, k, v);
  };

  /**
   * Gets the value at the given index.
   */
  public getAtIndex = (index) => this._get(index);

  /** Gets the min/max pair - as array for RGB */
  public getMinMax() {
    let min, max;
    const callback = ({ value: v }) => {
      const isArray = Array.isArray(v);
      if (min === undefined) {
        min = isArray ? [...v] : v;
        max = isArray ? [...v] : v;
      }
      if (isArray) {
        for (let i = 0; i < v.length; i++) {
          min[i] = Math.min(min[i], v[i]);
          max[i] = Math.max(max[i], v[i]);
        }
      } else {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    };
    this.forEach(callback, { boundsIJK: this.getDefaultBounds() });
    return { min, max };
  }

  /**
   * Sets the value at the given index
   */
  public setAtIndex = (index, v) => {
    const changed = this._set(index, v);
    if (changed !== false) {
      const pointIJK = this.toIJK(index);
      this.modifiedSlices.add(pointIJK[2]);
      VoxelManager.addBounds(this.boundsIJK, pointIJK);
      this.invalidateCachedScalarExpansion();
    }
    return changed;
  };

  /**
   * Drops a cached scalar-data expansion (see `getScalarData(true)`) after a
   * voxel write so the next read re-expands from the mutated backing store.
   * Only a flagged cached expansion is cleared - a real backing store (set via
   * the constructor or `setScalarData`, or kept fresh by `_updateScalarData`)
   * is left untouched - so this is a no-op (one boolean check) on the hot path
   * for the common case where nothing opted into caching.
   */
  private invalidateCachedScalarExpansion() {
    if (this._scalarDataIsCachedExpansion) {
      this.scalarData = null;
      this._scalarDataIsCachedExpansion = false;
    }
  }

  /**
   * Converts an index value to a Point3 IJK value
   */
  public toIJK(index: number): Point3 {
    return [
      index % this.width,
      Math.floor((index % this.frameSize) / this.width),
      Math.floor(index / this.frameSize),
    ];
  }

  public getMiddleSliceData = () => {
    const middleSliceIndex = Math.floor(this.dimensions[2] / 2);
    return this.getSliceData({
      sliceIndex: middleSliceIndex,
      slicePlane: 2,
    });
  };

  /**
   * Converts an IJK Point3 value to an index value
   */
  public toIndex(ijk: Point3) {
    return ijk[0] + ijk[1] * this.width + ijk[2] * this.frameSize;
  }

  public getDefaultBounds() {
    return this.dimensions.map((dimension) => [0, dimension - 1]) as BoundsIJK;
  }

  /**
   * Gets the bounds for the modified set of values.
   */
  public getBoundsIJK(): BoundsIJK {
    if (this.boundsIJK[0][0] < this.dimensions[0]) {
      return this.boundsIJK;
    }
    return this.getDefaultBounds();
  }

  /**
   * Iterates over the voxels in the VoxelManager and applies a callback function to each voxel.
   * It can operate on IJK and LPS coordinate systems, and it can be limited to a specific region
   * of the data if the isInObject function is provided.
   *
   * For the LPS calculations, both direction and spacing should be provided.
   *
   * If the boundsIJK is not provided, the iteration will be over the entire volume/data
   *
   * If the VoxelManager is backed by a Map, it will only iterate over the stored values.
   * Otherwise, it will iterate over all voxels within the specified or default bounds.
   *
   * @param callback - a callback to call with `value, index, pointIJK` for
   *     every point in the scalar data, map or rle map depending on the VoxelManager
   *     type.
   * @param options - has an optional isWIthinObject to test to see if hte callback
   *        should be called or not.
   */
  public forEach = (
    callback: (args: {
      value: unknown;
      index: number;
      pointIJK: Point3;
      pointLPS: Point3;
    }) => void,
    options: {
      boundsIJK?: BoundsIJK;
      isInObject?: (pointLPS, pointIJK) => boolean;
      returnPoints?: boolean;
      imageData?: vtkImageData | CPUImageData;
    } = {}
  ) => {
    const isInObjectBoundsIJK = options.boundsIJK || this.getBoundsIJK();
    const isInObject = options.isInObject || this.isInObject || (() => true);
    const returnPoints = options.returnPoints || false;

    const useLPSTransform = options.imageData;

    const iMin = Math.min(isInObjectBoundsIJK[0][0], isInObjectBoundsIJK[0][1]);
    const iMax = Math.max(isInObjectBoundsIJK[0][0], isInObjectBoundsIJK[0][1]);
    const jMin = Math.min(isInObjectBoundsIJK[1][0], isInObjectBoundsIJK[1][1]);
    const jMax = Math.max(isInObjectBoundsIJK[1][0], isInObjectBoundsIJK[1][1]);
    const kMin = Math.min(isInObjectBoundsIJK[2][0], isInObjectBoundsIJK[2][1]);
    const kMax = Math.max(isInObjectBoundsIJK[2][0], isInObjectBoundsIJK[2][1]);

    const pointsInShape = [];

    if (useLPSTransform) {
      const pointsInShape = iterateOverPointsInShapeVoxelManager({
        voxelManager: this,
        imageData: options.imageData,
        bounds: [
          [iMin, iMax],
          [jMin, jMax],
          [kMin, kMax],
        ],
        pointInShapeFn: isInObject,
        callback,
        returnPoints,
      });
      return pointsInShape;
    }

    // We don't need the complex LPS calculations and we can just iterate over the data
    // in the IJK coordinate system
    if (this.map) {
      if (this.map instanceof RLEVoxelMap) {
        return this.rleForEach(callback, options);
      }
      // Optimize this for only values in the map
      for (const index of this.map.keys()) {
        const pointIJK = this.toIJK(index);
        if (!isInObject(null, pointIJK)) {
          continue;
        }
        const value = this._get(index);

        if (returnPoints) {
          pointsInShape.push({
            value,
            index,
            pointIJK,
            pointLPS: null,
          });
        }

        callback({ value, index, pointIJK, pointLPS: null });
      }

      return pointsInShape;
    } else {
      for (let k = kMin; k <= kMax; k++) {
        const kIndex = k * this.frameSize;
        for (let j = jMin; j <= jMax; j++) {
          const jIndex = kIndex + j * this.width;
          for (let i = iMin, index = jIndex + i; i <= iMax; i++, index++) {
            const value = this.getAtIndex(index);
            const pointIJK = [i, j, k];

            if (!isInObject(null, pointIJK)) {
              continue;
            }

            if (returnPoints) {
              pointsInShape.push({
                value,
                index,
                pointIJK,
                pointLPS: null,
              });
            }
            callback({ value, index, pointIJK: [i, j, k], pointLPS: null });
          }
        }
      }

      return pointsInShape;
    }
  };

  /**
   * Foreach callback optimized for RLE testing
   * @param callback - a callback to call with `value, index, pointIJK` for
   *     every point in the rle map (see the rle map for callbacks that work at
   *     the row or rle level, as those can be faster/more efficient)
   * @param options - has an optional isWIthinObject to test to see if hte callback
   *        should be called or not.
   */
  public rleForEach(callback, options?) {
    const boundsIJK = options?.boundsIJK || this.getBoundsIJK();
    const { isWithinObject } = options || {};
    const map = this.map as RLEVoxelMap<T>;

    if (!map) {
      log.warn(
        'No map found, you need to use a map voxel manager to use rleForEach'
      );
      return;
    }

    // NOTE: this used to reset `map.defaultValue` to undefined. Nothing here
    // reads through `get`, so the reset only had the effect of undoing a
    // default the map's creator had chosen (a labelmap map, for instance, sets
    // 0 so an unsegmented voxel reads as background rather than undefined).
    for (let k = boundsIJK[2][0]; k <= boundsIJK[2][1]; k++) {
      for (let j = boundsIJK[1][0]; j <= boundsIJK[1][1]; j++) {
        const row = map.getRun(j, k);
        if (!row) {
          continue;
        }
        for (const rle of row) {
          const { start, end, value } = rle;
          const baseIndex = this.toIndex([0, j, k]);
          for (let i = start; i < end; i++) {
            const callbackArguments = {
              value,
              index: baseIndex + i,
              pointIJK: [i, j, k],
            };
            if (isWithinObject?.(callbackArguments) === false) {
              continue;
            }
            callback(callbackArguments);
          }
        }
      }
    }
  }

  /**
   * Retrieves the scalar data.
   * If the scalar data is already available, it will be returned.
   * Otherwise, if the `_getScalarData` method is defined, it will be called to retrieve the scalar data.
   * If neither the scalar data nor the `_getScalarData` method is available, an error will be thrown.
   *
   * @param storeScalarData - a parameter to allow storing the scalar data rather than throwing it away
   *     each time.
   * @returns The scalar data.
   * @throws {Error} If no scalar data is available.
   */
  public getScalarData(storeScalarData = false): PixelDataTypedArray {
    if (this.scalarData) {
      this._updateScalarData?.(this.scalarData);
      return this.scalarData;
    }

    if (this._getScalarData) {
      const scalarData = this._getScalarData();
      if (storeScalarData) {
        // Retain the expanded scalar data so subsequent calls return it
        // directly instead of re-expanding (e.g. re-running the RLE decode)
        // on every access. Callers opt in because this trades memory for speed.
        // Flag it so a later voxel write can invalidate this snapshot (the
        // backing store is the source of truth) instead of returning stale data.
        this.scalarData = scalarData as PixelDataTypedArray;
        this._scalarDataIsCachedExpansion = true;
      }
      return scalarData as PixelDataTypedArray;
    }

    throw new Error('No scalar data available');
  }

  public setScalarData(newScalarData: PixelDataTypedArray) {
    // An explicitly installed backing store is the source of truth, not a
    // cached expansion, so it must not be invalidated on later writes.
    this.scalarData = newScalarData;
    this._scalarDataIsCachedExpansion = false;
  }

  /**
   * The backing store that a caller can write into - the array that
   * `getScalarData()` both returns and reads back from - or undefined when this
   * manager has none.
   *
   * A manager that keeps its voxels in something else (an RLE map) can still
   * produce an array from `getScalarData()`, but that is a fresh expansion:
   * reads of it are a snapshot and writes into it are discarded. A caller with
   * a whole frame to read or fill wants to know which one it has, so that it
   * can touch the array directly where that is correct and only fall back to a
   * call per voxel (`getAtIndex` / `setAtIndex`, or `setFromScalarData`) where
   * it is not.
   *
   * Undefined is the normal answer for an RLE labelmap, not an error - every
   * caller needs the per voxel fallback.
   */
  public getWritableScalarData(): PixelDataTypedArray | undefined {
    return this._scalarDataIsCachedExpansion
      ? undefined
      : (this.scalarData ?? undefined);
  }

  /**
   * Copies `scalarData` INTO this voxel manager, replacing what it holds, in
   * whatever representation it actually uses.
   *
   * Use this rather than `getScalarData().set(...)`: on a manager whose data is
   * not a typed array (an RLE map, say) `getScalarData()` returns a throwaway
   * expansion, so writing into it silently discards the update. Unlike
   * `setScalarData`, this does not install `scalarData` as the backing store, so
   * an RLE map stays the source of truth for later reads and edits.
   */
  public setFromScalarData(scalarData: ArrayLike<number>): void {
    const map = this.map;

    if (map instanceof RLEVoxelMap) {
      const [width, height, depth] = this.dimensions;
      // The value an unwritten voxel reads back as, and so the one that costs
      // nothing to leave out of the runs. It is the map's default rather than a
      // literal zero: on a map defaulting to 5, dropping the 0s would make them
      // read back as 5, and it is the 5s that are free to drop instead.
      const sparseValue = (map.defaultValue ?? 0) as number;
      // fillFrom appends runs to the rows it finds, so start from empty to get
      // replace-the-whole-thing semantics.
      map.clear();
      map.fillFrom(
        (i, j, k) => {
          const value = scalarData[i + j * width + k * this.frameSize];
          // fillFrom skips undefined, which is what keeps the map sparse.
          return (value === sparseValue ? undefined : value) as T;
        },
        [
          [0, width - 1],
          [0, height - 1],
          [0, (depth ?? 1) - 1],
        ]
      );
      this.invalidateCachedScalarExpansion();
      return;
    }

    this.getScalarData().set(scalarData);
  }

  /**
   * Gets the length of the scalar data, i.e. of the array `getScalarData()`
   * would produce.
   *
   * A manager that keeps its voxels in something other than an array (an RLE
   * map, say) has nothing to measure, but its length is fixed by the geometry -
   * so it is derived from the dimensions rather than reported as unavailable.
   * Without that, anything sizing a buffer from a voxel manager
   * (`getCompleteScalarDataArray`, and so segmentation statistics) throws for
   * those managers.
   *
   * @returns The length of the scalar data.
   * @throws {Error} If the length can be neither measured nor derived.
   */
  public getScalarDataLength() {
    if (this.scalarData) {
      return this.scalarData.length;
    }

    if (this._getScalarDataLength) {
      return this._getScalarDataLength();
    }

    const [width, height, depth] = this.dimensions ?? [];
    if (width && height) {
      return width * height * (depth || 1) * this.numberOfComponents;
    }

    throw new Error('No scalar data available');
  }

  public get sizeInBytes(): number {
    return this.getScalarDataLength() * this.bytePerVoxel;
  }

  public get bytePerVoxel(): number {
    if (this.scalarData) {
      return this.scalarData.BYTES_PER_ELEMENT;
    }

    // No backing array, but a manager that can say which array type it expands
    // to (again, RLE) knows the width of a voxel from its constructor.
    const Constructor = this._getConstructor?.() as unknown as {
      BYTES_PER_ELEMENT?: number;
    };
    if (Constructor?.BYTES_PER_ELEMENT) {
      return Constructor.BYTES_PER_ELEMENT;
    }

    // An RLE map whose creator chose no type still expands to a known array
    // type, so it still has a voxel width. The `_get(0)` fallback below cannot
    // find that width: an RLE map holds numbers, and a number has no
    // BYTES_PER_ELEMENT, so an empty map throws on `undefined` and a map with a
    // default returns NaN. Both reach `sizeInBytes` now that
    // `getScalarDataLength()` derives a length rather than throwing first.
    if (this.map instanceof RLEVoxelMap) {
      const ExpansionConstructor = this.map.pixelDataConstructor
        ? this.map.pixelDataConstructor
        : Uint8ClampedArray;
      return ExpansionConstructor.BYTES_PER_ELEMENT;
    }

    // get the first element of the scalar data
    const value = this._get(0) as unknown as { BYTES_PER_ELEMENT: number };
    return value.BYTES_PER_ELEMENT;
  }

  public clearBounds() {
    this.boundsIJK.map((bound) => {
      bound[0] = Infinity;
      bound[1] = -Infinity;
    });
  }

  /**
   * Clears any map specific data, as well as the modified slices, points and
   * bounds.
   */
  public clear() {
    this.map?.clear();
    this.clearBounds();
    this.modifiedSlices.clear();
    this.points?.clear();
  }

  /**
   * @returns a constructor for a typed array for the pixel data of the given length
   *     and the right type.  Defaults to float32array.
   */
  public getConstructor(): new (length: number) => PixelDataTypedArray {
    if (this.scalarData) {
      return this.scalarData.constructor as new (
        length: number
      ) => PixelDataTypedArray;
    }

    if (this._getConstructor) {
      // A volume that streams its images answers with nothing until the first
      // image is cached, so the fallback below applies to that volume too.
      const Constructor = this._getConstructor();

      if (Constructor) {
        return Constructor as new (length: number) => PixelDataTypedArray;
      }
    }

    log.warn('No scalar data available or can be used to get the constructor');

    // Return a default constructor (e.g., Float32Array) if no constructor is available
    return Float32Array as new (length: number) => PixelDataTypedArray;
  }

  /**
   * @returns The array of modified k indices
   */
  public getArrayOfModifiedSlices(): number[] {
    return Array.from(this.modifiedSlices);
  }

  /**
   * Resets the set of modified slices.
   * This method clears all entries from the `modifiedSlices` set,
   * effectively marking all slices as unmodified.
   */
  public resetModifiedSlices(): void {
    this.modifiedSlices.clear();
  }

  /**
   * Sets the bounds of the voxel manager.
   * @param bounds - The bounds to set.
   */
  public setBounds(bounds: BoundsIJK) {
    this.boundsIJK = bounds;
  }

  /**
   * Extends the bounds of this object to include the specified point
   */
  public static addBounds(bounds: BoundsIJK, point: Point3) {
    if (!bounds) {
      bounds = [
        [Infinity, -Infinity],
        [Infinity, -Infinity],
        [Infinity, -Infinity],
      ];
    }

    // Directly update the bounds for each axis
    bounds[0][0] = Math.min(point[0], bounds[0][0]);
    bounds[0][1] = Math.max(point[0], bounds[0][1]);
    bounds[1][0] = Math.min(point[1], bounds[1][0]);
    bounds[1][1] = Math.max(point[1], bounds[1][1]);
    bounds[2][0] = Math.min(point[2], bounds[2][0]);
    bounds[2][1] = Math.max(point[2], bounds[2][1]);
  }

  private static getWorldToIndexTransform(
    volumeGeometry: VoxelVolumeGeometry
  ): mat3 {
    const cachedTransform = worldToIndexTransformCache.get(
      volumeGeometry as object
    );

    if (cachedTransform) {
      return cachedTransform;
    }

    const direction = volumeGeometry.direction;
    const spacing = volumeGeometry.spacing;
    const transform = mat3.fromValues(
      direction[0] / spacing[0],
      direction[3] / spacing[1],
      direction[6] / spacing[2],
      direction[1] / spacing[0],
      direction[4] / spacing[1],
      direction[7] / spacing[2],
      direction[2] / spacing[0],
      direction[5] / spacing[1],
      direction[8] / spacing[2]
    );

    worldToIndexTransformCache.set(volumeGeometry as object, transform);

    return transform;
  }

  public static worldToIndexContinuous(
    volumeGeometry: VoxelVolumeGeometry,
    worldPos: Point3
  ): Point3 {
    const origin = volumeGeometry.origin;
    worldToIndexDeltaScratch[0] = worldPos[0] - origin[0];
    worldToIndexDeltaScratch[1] = worldPos[1] - origin[1];
    worldToIndexDeltaScratch[2] = worldPos[2] - origin[2];
    const continuousIndex = vec3.transformMat3(
      worldToIndexResultScratch,
      worldToIndexDeltaScratch,
      VoxelManager.getWorldToIndexTransform(volumeGeometry)
    );

    return [continuousIndex[0], continuousIndex[1], continuousIndex[2]];
  }

  public static sampleAtWorldCoordinates(
    volume: SampleableVoxelVolume,
    worldX: number,
    worldY: number,
    worldZ: number,
    interpolationType: InterpolationType = InterpolationType.LINEAR
  ): number {
    if (!volume.voxelManager) {
      return NaN;
    }

    const origin = volume.origin;
    worldToIndexDeltaScratch[0] = worldX - origin[0];
    worldToIndexDeltaScratch[1] = worldY - origin[1];
    worldToIndexDeltaScratch[2] = worldZ - origin[2];
    const continuousIndex = vec3.transformMat3(
      worldToIndexResultScratch,
      worldToIndexDeltaScratch,
      VoxelManager.getWorldToIndexTransform(volume)
    );

    return VoxelManager.sampleAtContinuousCoordinates(
      volume.voxelManager,
      volume.dimensions,
      continuousIndex[0],
      continuousIndex[1],
      continuousIndex[2],
      interpolationType
    );
  }

  public static sampleAtWorld(
    volume: SampleableVoxelVolume,
    worldPos: Point3,
    interpolationType: InterpolationType = InterpolationType.LINEAR
  ): number {
    return VoxelManager.sampleAtWorldCoordinates(
      volume,
      worldPos[0],
      worldPos[1],
      worldPos[2],
      interpolationType
    );
  }

  public static sampleAtContinuousIndex(
    voxelManager: IVoxelManager<number> | IVoxelManager<RGB>,
    dimensions: Point3,
    continuousIndex: Point3,
    interpolationType: InterpolationType = InterpolationType.LINEAR
  ): number {
    return VoxelManager.sampleAtContinuousCoordinates(
      voxelManager,
      dimensions,
      continuousIndex[0],
      continuousIndex[1],
      continuousIndex[2],
      interpolationType
    );
  }

  private static sampleAtContinuousCoordinates(
    voxelManager: IVoxelManager<number> | IVoxelManager<RGB>,
    dimensions: Point3,
    iC: number,
    jC: number,
    kC: number,
    interpolationType: InterpolationType
  ): number {
    return interpolationType === InterpolationType.NEAREST
      ? VoxelManager.sampleNearestAtContinuousCoordinates(
          voxelManager,
          dimensions,
          iC,
          jC,
          kC
        )
      : VoxelManager.sampleLinearAtContinuousCoordinates(
          voxelManager,
          dimensions,
          iC,
          jC,
          kC
        );
  }

  private static sampleNearestAtContinuousCoordinates(
    voxelManager: IVoxelManager<number> | IVoxelManager<RGB>,
    dimensions: Point3,
    iC: number,
    jC: number,
    kC: number
  ): number {
    const i = Math.floor(iC + 0.5 - 1e-6);
    const j = Math.floor(jC + 0.5 - 1e-6);
    const k = Math.floor(kC + 0.5 - 1e-6);
    const dx = dimensions[0];
    const dy = dimensions[1];
    const dz = dimensions[2];

    if (i < 0 || i >= dx || j < 0 || j >= dy || k < 0 || k >= dz) {
      return NaN;
    }

    return Number(voxelManager.getAtIJK(i, j, k));
  }

  private static sampleLinearAtContinuousCoordinates(
    voxelManager: IVoxelManager<number> | IVoxelManager<RGB>,
    dimensions: Point3,
    i: number,
    j: number,
    k: number
  ): number {
    const dx = dimensions[0];
    const dy = dimensions[1];
    const dz = dimensions[2];

    if (i < 0 || i > dx - 1 || j < 0 || j > dy - 1 || k < 0 || k > dz - 1) {
      return NaN;
    }

    const i0 = Math.floor(i);
    const j0 = Math.floor(j);
    const k0 = Math.floor(k);
    const i1 = Math.min(i0 + 1, dx - 1);
    const j1 = Math.min(j0 + 1, dy - 1);
    const k1 = Math.min(k0 + 1, dz - 1);
    const di = i - i0;
    const dj = j - j0;
    const dk = k - k0;
    const oneMinusDi = 1 - di;
    const oneMinusDj = 1 - dj;
    const oneMinusDk = 1 - dk;
    const c000 = Number(voxelManager.getAtIJK(i0, j0, k0));
    const c100 = Number(voxelManager.getAtIJK(i1, j0, k0));
    const c010 = Number(voxelManager.getAtIJK(i0, j1, k0));
    const c110 = Number(voxelManager.getAtIJK(i1, j1, k0));
    const c001 = Number(voxelManager.getAtIJK(i0, j0, k1));
    const c101 = Number(voxelManager.getAtIJK(i1, j0, k1));
    const c011 = Number(voxelManager.getAtIJK(i0, j1, k1));
    const c111 = Number(voxelManager.getAtIJK(i1, j1, k1));
    const c00 = c000 * oneMinusDi + c100 * di;
    const c10 = c010 * oneMinusDi + c110 * di;
    const c01 = c001 * oneMinusDi + c101 * di;
    const c11 = c011 * oneMinusDi + c111 * di;
    const c0 = c00 * oneMinusDj + c10 * dj;
    const c1 = c01 * oneMinusDj + c11 * dj;

    return c0 * oneMinusDk + c1 * dk;
  }

  /**
   * Adds a point as an array or an index value to the set of points
   * associated with this voxel value.
   * Can be used for tracking clicked points or other modified values.
   */
  public addPoint(point: Point3 | number) {
    const index = Array.isArray(point)
      ? point[0] + this.width * point[1] + this.frameSize * point[2]
      : point;
    if (!this.points) {
      this.points = new Set<number>();
    }
    this.points.add(index);
  }

  /**
   * Gets the list of added points as an array of Point3 values
   */
  public getPoints(): Point3[] {
    return this.points
      ? [...this.points].map((index) => this.toIJK(index))
      : [];
  }

  /**
   * Retrieves the slice data for a given slice view.
   *
   * @param sliceViewInfo - An object containing information about the slice view.
   * @param sliceViewInfo.sliceIndex - The index of the slice.
   * @param sliceViewInfo.slicePlane - The axis of the slice (0 for YZ plane, 1 for XZ plane, 2 for XY plane).
   * @returns A typed array containing the pixel data for the specified slice.
   * @throws Error if an invalid slice axis is provided.
   */
  public getSliceData = ({
    sliceIndex,
    slicePlane,
  }: {
    sliceIndex: number;
    slicePlane: number;
  }): PixelDataTypedArray => {
    const direct = this._getSliceData?.({ sliceIndex, slicePlane });

    if (direct) {
      return direct;
    }

    const [width, height, depth] = this.dimensions;
    const frameSize = width * height;
    const startIndex = sliceIndex * frameSize;

    let sliceSize: number;
    const SliceDataConstructor = this.getConstructor();

    function isValidConstructor(
      ctor: unknown
    ): ctor is new (length: number) => PixelDataTypedArray {
      return typeof ctor === 'function';
    }

    if (!isValidConstructor(SliceDataConstructor)) {
      // Return an empty typed array instead of an empty regular array
      return new Uint8Array(0) as PixelDataTypedArray;
    }

    // Todo: optimize it when we have scalar data
    let sliceData: PixelDataTypedArray;
    switch (slicePlane) {
      case 0: // YZ plane
        sliceSize = height * depth;
        sliceData = new SliceDataConstructor(sliceSize);
        for (let i = 0; i < height; i++) {
          for (let j = 0; j < depth; j++) {
            const index = sliceIndex + i * width + j * frameSize;
            this.setSliceDataValue(sliceData, i + j * height, this._get(index));
          }
        }
        break;
      case 1: // XZ plane
        sliceSize = width * depth;
        sliceData = new SliceDataConstructor(sliceSize);
        for (let i = 0; i < width; i++) {
          for (let j = 0; j < depth; j++) {
            const index = i + sliceIndex * width + j * frameSize;
            this.setSliceDataValue(sliceData, i + j * width, this._get(index));
          }
        }
        break;
      case 2: // XY plane
        sliceSize = width * height;
        sliceData = new SliceDataConstructor(sliceSize);
        for (let i = 0; i < sliceSize; i++) {
          this.setSliceDataValue(sliceData, i, this._get(startIndex + i));
        }
        break;
      default:
        throw new Error(
          'Oblique plane - todo - implement as ortho normal vector'
        );
    }

    return sliceData;
  };

  private setSliceDataValue(
    sliceData: PixelDataTypedArray,
    index: number,
    value: T
  ): void {
    if (Array.isArray(value)) {
      // Handle RGB values
      for (let i = 0; i < value.length; i++) {
        sliceData[index * value.length + i] = this.toNumber(value[i]);
      }
    } else {
      // Handle single number values
      sliceData[index] = this.toNumber(value);
    }
  }

  private toNumber(value: T | number): number {
    if (typeof value === 'number') {
      return value;
    }
    if (Array.isArray(value)) {
      return value[0] || 0;
    }
    return 0;
  }

  /**
   * Creates a voxel manager backed by an array of scalar data having the
   * given number of components.
   * Note that the number of components can be larger than three, in case data
   * is stored in additional pixels.  However, the return type is still RGB.
   */
  private static _createRGBScalarVolumeVoxelManager({
    dimensions,
    scalarData,
    numberOfComponents = 3,
    id,
  }: {
    dimensions: Point3;
    scalarData;
    numberOfComponents;
    id?: string;
  }): VoxelManager<RGB> {
    const voxels = new VoxelManager<RGB>(dimensions, {
      _get: (index) => {
        index *= numberOfComponents;
        return [
          scalarData[index++],
          scalarData[index++],
          scalarData[index++],
        ] as RGB;
      },
      _id: id || '_createRGBScalarVolumeVoxelManager',
      _set: (index, v) => {
        index *= 3;
        const isChanged = !isEqual(scalarData[index], v);
        scalarData[index++] = v[0];
        scalarData[index++] = v[1];
        scalarData[index++] = v[2];
        return isChanged;
      },
      numberOfComponents,
      scalarData,
    });

    voxels.clear = () => {
      scalarData.fill(0);
    };
    return voxels;
  }

  /**
   * Creates a VoxelManager for an image volume. which are those volumes
   * that are composed of multiple images, one for each slice.
   * @param dimensions - The dimensions of the image volume.
   * @param imageIds - The array of image IDs.
   * @param dataType - the type that the images of this volume hold. The volume
   *     states it from its metadata, so `getConstructor` answers with the right
   *     type before the first image arrives.
   * @returns A VoxelManager instance for the image volume.
   */
  public static createImageVolumeVoxelManager({
    dimensions,
    imageIds,
    numberOfComponents = 1,
    id,
    dataType,
  }: {
    dimensions: Point3;
    imageIds: string[];
    numberOfComponents: number;
    id?: string;
    dataType?: PixelDataTypedArrayString;
  }): IVolumeVoxelManager<number> | IVolumeVoxelManager<RGB> {
    const [width, height, depth] = dimensions;
    const pixelsPerSlice = width * height;
    // The slice voxel managers are typed as the CLASS and not as the interface,
    // because `setCompleteScalarDataArray` below reads the private
    // `scalarData` field of a slice voxel manager. Every image voxel manager is
    // an instance of this class, so the cast at `resolveSliceVoxelManager`
    // states a fact.
    const sliceVoxelManagers = new Array<
      VoxelManager<number> | VoxelManager<RGB> | null | undefined
    >(depth);
    // The image generation of the cache when a slice found no image. A miss is
    // reused only while no image has reached the cache since, so a frame that
    // arrives without a call to invalidateSlice still becomes visible.
    const sliceMissGenerations = new Array<number>(depth);
    let lastSliceIndex = -1;
    let lastSliceVoxelManager: VoxelManager<number> | VoxelManager<RGB> | null =
      null;
    const warnedMissingImageIds = new Set<number>();
    const warnedMissingImages = new Set<string>();
    // One entry for each slice whose image holds a different number of voxels
    // in the plane from this volume. `undefined` means that the slice has not
    // been resolved, and `null` that the image matches the volume exactly.
    const sliceScalings = new Array<SliceScaling | null | undefined>(depth);
    const scalingsBySize = new Map<string, SliceScaling>();

    /**
     * The lookup that takes a column and a row of this volume to a column and a
     * row of an image of a different size.
     *
     * The image loader gives an image its true decoded size, which a
     * sub-resolution decode and a thumbnail make smaller than the volume. This
     * repeats the arithmetic of the `replicate` scaling of that loader, which
     * maps the two extents end to end, so a read gives the same value that an
     * up-scale at the moment of the decode gave.
     */
    const scalingFor = (
      imageWidth: number,
      imageHeight: number
    ): SliceScaling => {
      const key = `${imageWidth}x${imageHeight}`;
      const cached = scalingsBySize.get(key);

      if (cached) {
        return cached;
      }

      const columns = new Int32Array(width);
      const rows = new Int32Array(height);

      for (let i = 0; i < width; i++) {
        columns[i] =
          width > 1 ? Math.floor((i * (imageWidth - 1)) / (width - 1)) : 0;
      }

      for (let j = 0; j < height; j++) {
        const row =
          height > 1 ? Math.floor((j * (imageHeight - 1)) / (height - 1)) : 0;
        rows[j] = row * imageWidth;
      }

      const scaling = { columns, rows };
      scalingsBySize.set(key, scaling);

      return scaling;
    };

    /** The index in the image that holds the value of an index of a slice. */
    const scaleIndex = (scaling: SliceScaling, pixelIndex: number): number => {
      const row = Math.floor(pixelIndex / width);

      return scaling.rows[row] + scaling.columns[pixelIndex - row * width];
    };

    const resolveSliceVoxelManager = (
      sliceIndex: number
    ): VoxelManager<number> | VoxelManager<RGB> | null => {
      if (sliceIndex < 0 || sliceIndex >= depth) {
        return null;
      }

      const cachedVoxelManager = sliceVoxelManagers[sliceIndex];
      if (cachedVoxelManager) {
        return cachedVoxelManager;
      }
      if (
        cachedVoxelManager === null &&
        sliceMissGenerations[sliceIndex] === cache.getImageGeneration()
      ) {
        return null;
      }

      const imageId = imageIds[sliceIndex];
      if (!imageId) {
        if (!warnedMissingImageIds.has(sliceIndex)) {
          warnedMissingImageIds.add(sliceIndex);
          log.warn(`ImageId not found for sliceIndex: ${sliceIndex}`);
        }
        sliceVoxelManagers[sliceIndex] = null;
        sliceMissGenerations[sliceIndex] = cache.getImageGeneration();
        return null;
      }

      const image = cache.getImage(imageId);
      if (!image?.voxelManager) {
        // A frame that has not arrived is the ordinary state of a streaming
        // volume, and not a defect: `getAtIJK` answers with the nearest value
        // that it holds, and a box reduction takes the source voxels that have
        // arrived and skips the rest. A derivation that follows the load reads
        // every frame of a box as soon as one frame of that box arrives, so a
        // warning here reports the normal case once for each image of the
        // volume. The message stays available at the debug level.
        if (!warnedMissingImages.has(imageId)) {
          warnedMissingImages.add(imageId);
          log.debug(`Image not found for imageId: ${imageId}`);
        }

        // THE ABSENCE IS CACHED, exactly as the absence of an image id is. A
        // reduction reads every voxel of a slice, so without this each of the
        // 262144 reads of one slice of 512 x 512 asks the cache for the same
        // image again. `invalidateSlice` clears the entry, and
        // `ImageVolume.markFrameTexturesDirty` calls it when the image of the
        // frame arrives. The next image that reaches the cache also expires the
        // entry, so a frame that arrives with no such call still shows.
        sliceVoxelManagers[sliceIndex] = null;
        sliceMissGenerations[sliceIndex] = cache.getImageGeneration();

        return null;
      }

      const imageVoxelManager = image.voxelManager as
        | VoxelManager<number>
        | VoxelManager<RGB>;
      const imageWidth = image.width ?? imageVoxelManager.dimensions?.[0];
      const imageHeight = image.height ?? imageVoxelManager.dimensions?.[1];

      sliceVoxelManagers[sliceIndex] = imageVoxelManager;
      // The image loader gives an image its true decoded size, so a
      // sub-resolution frame and a thumbnail hold fewer voxels than one slice
      // of this volume. The image stays in the cache exactly as the loader
      // provided it, and a read of this volume scales it.
      sliceScalings[sliceIndex] =
        imageWidth === width && imageHeight === height
          ? null
          : scalingFor(imageWidth, imageHeight);

      return imageVoxelManager;
    };

    /**
     * The index in the image of a slice that holds the value of an index in
     * that slice of this volume.
     */
    const imageIndexOf = (sliceIndex: number, pixelIndex: number): number => {
      const scaling = sliceScalings[sliceIndex];

      return scaling ? scaleIndex(scaling, pixelIndex) : pixelIndex;
    };

    function getVoxelValue(index) {
      const sliceIndex = Math.floor(index / pixelsPerSlice);
      if (sliceIndex < 0 || sliceIndex >= depth) {
        return null;
      }
      let imageVoxelManager: VoxelManager<number> | VoxelManager<RGB> | null;

      if (sliceIndex === lastSliceIndex && lastSliceVoxelManager) {
        imageVoxelManager = lastSliceVoxelManager;
      } else {
        // A slice that holds no image resolves again, which is cheap:
        // `resolveSliceVoxelManager` remembers the miss until an image reaches
        // the cache, and the next image may be the image of this slice.
        imageVoxelManager = resolveSliceVoxelManager(sliceIndex);
        lastSliceIndex = sliceIndex;
        lastSliceVoxelManager = imageVoxelManager;
      }

      if (!imageVoxelManager) {
        return null;
      }

      const pixelIndex = imageIndexOf(
        sliceIndex,
        index - sliceIndex * pixelsPerSlice
      );

      return imageVoxelManager.getAtIndex(pixelIndex) as number | RGB;
    }

    function setVoxelValue(index, v) {
      const sliceIndex = Math.floor(index / pixelsPerSlice);
      if (sliceIndex < 0 || sliceIndex >= depth) {
        return false;
      }
      let imageVoxelManager: VoxelManager<number> | VoxelManager<RGB> | null;

      if (sliceIndex === lastSliceIndex && lastSliceVoxelManager) {
        imageVoxelManager = lastSliceVoxelManager;
      } else {
        imageVoxelManager = resolveSliceVoxelManager(sliceIndex);
        lastSliceIndex = sliceIndex;
        lastSliceVoxelManager = imageVoxelManager;
      }

      if (!imageVoxelManager) {
        return false;
      }

      const pixelIndex = imageIndexOf(
        sliceIndex,
        index - sliceIndex * pixelsPerSlice
      );

      const currentValue = imageVoxelManager.getAtIndex(pixelIndex);
      const isChanged = !isEqual(v, currentValue);

      if (!isChanged) {
        return isChanged;
      }
      imageVoxelManager.setAtIndex(pixelIndex, v as number);

      return true;
    }

    const _getConstructor = () => {
      const imageVoxelManager = resolveSliceVoxelManager(0);

      if (imageVoxelManager) {
        return imageVoxelManager.getConstructor();
      }

      // No image has arrived yet. The volume states the type of its images from
      // the metadata, so a derivation that runs before the load can still
      // allocate the right type. Without this answer the caller takes the
      // Float32Array of the default, which holds twice the bytes of the
      // Int16Array that a CT needs.
      return dataType ? getConstructorFromType(dataType, false) : null;
    };

    const voxelManager = new VoxelManager<number | RGB>(dimensions, {
      _get: getVoxelValue,
      _set: setVoxelValue,
      numberOfComponents,
      _getConstructor,
      _id: id || 'createImageVolumeVoxelManager',
    });

    voxelManager.invalidateCache = () => {
      sliceVoxelManagers.fill(undefined);
      sliceScalings.fill(undefined);
      lastSliceIndex = -1;
      lastSliceVoxelManager = null;
    };

    /**
     * Forgets the voxel manager that this volume holds for one slice.
     *
     * The image of a slice can be replaced. A progressive loader delivers a
     * replicate of a nearby frame first, which the cache holds under the image
     * id of this slice, and it delivers the image of the slice itself later.
     * Without this the volume keeps the voxel manager of the replicate for
     * ever, and the slice shows the voxels of another slice: a sagittal or a
     * coronal view then holds a block of one frame repeated.
     */
    voxelManager.invalidateSlice = (sliceIndex: number) => {
      if (sliceIndex < 0 || sliceIndex >= depth) {
        return;
      }

      sliceVoxelManagers[sliceIndex] = undefined;
      sliceScalings[sliceIndex] = undefined;

      if (lastSliceIndex === sliceIndex) {
        lastSliceIndex = -1;
        lastSliceVoxelManager = null;
      }
    };

    // One k slice of this volume is one image of the cache, and that image
    // already holds the values of the slice together and in order. Copy them,
    // rather than read the slice voxel by voxel: a volume of 512 x 512 x 1232
    // holds 323 million voxels, and one call for each of them takes tens of
    // seconds.
    //
    // The copy keeps the contract of `getSliceData`, which gives the caller an
    // array of its own. It also keeps the image of the cache exactly as the
    // loader provided it, which a caller that writes to the result would
    // otherwise change.
    voxelManager._getSliceData = ({ sliceIndex, slicePlane }) => {
      if (slicePlane !== 2) {
        // A YZ or an XZ slice reads one voxel of every image, so no image
        // holds the values of that slice together.
        return undefined;
      }

      const imageVoxelManager = resolveSliceVoxelManager(sliceIndex);

      if (!imageVoxelManager || numberOfComponents !== 1) {
        // The image has not arrived, or one voxel of it holds more than one
        // value. The caller composes the slice itself.
        return undefined;
      }

      // An image that keeps its values in a run length map (an RLE labelmap)
      // has no array. One expansion of the map is a new array already, and it
      // costs far less than a read per voxel.
      const stored = imageVoxelManager.scalarData;
      const scalarData =
        stored ??
        (imageVoxelManager._getScalarData
          ? imageVoxelManager.getScalarData()
          : undefined);

      if (!scalarData) {
        return undefined;
      }

      const scaling = sliceScalings[sliceIndex];

      if (!scaling) {
        return (stored ? stored.slice() : scalarData) as PixelDataTypedArray;
      }

      // The image holds a different number of voxels from the slice of this
      // volume, so scale it rather than copy it.
      const slice = new (scalarData.constructor as new (
        length: number
      ) => PixelDataTypedArray)(pixelsPerSlice);

      for (let j = 0; j < height; j++) {
        const sourceRow = scaling.rows[j];
        const targetRow = j * width;

        for (let i = 0; i < width; i++) {
          slice[targetRow + i] = scalarData[sourceRow + scaling.columns[i]];
        }
      }

      return slice;
    };

    // The scalar data of the cached image of one k slice, with no copy. A
    // caller must not write to it. It gives nothing when the image has not
    // arrived, when the image needs scaling to the slice, or when a voxel holds
    // more than one value, and the caller then takes `_getSliceData`.
    voxelManager._getSliceView = (sliceIndex: number) => {
      if (numberOfComponents !== 1) {
        return undefined;
      }

      const scalarData = resolveSliceVoxelManager(sliceIndex)?.scalarData;

      // The resolution states the scaling of the slice, so read it after.
      return sliceScalings[sliceIndex] ? undefined : scalarData;
    };

    voxelManager.getMiddleSliceData = () => {
      const middleSliceIndex = Math.floor(dimensions[2] / 2);
      return voxelManager.getSliceData({
        sliceIndex: middleSliceIndex,
        slicePlane: 2,
      });
    };

    voxelManager.clear = () => {
      for (const imageId of imageIds) {
        const image = cache.getImage(imageId);
        image.voxelManager.clear();
      }
    };

    // Todo: need a way to make it understand dirty status if pixel data is changed
    voxelManager.getRange = () => {
      // get all the pixel data
      let minValue = Infinity;
      let maxValue = -Infinity;
      for (const imageId of imageIds) {
        const image = cache.getImage(imageId);

        // Skip if image not loaded yet
        if (!image) {
          continue;
        }

        // min and max pixel value is correct, //todo this is not true
        // for dynamically changing data such as labelmaps in segmentation
        if (image.minPixelValue < minValue) {
          minValue = image.minPixelValue;
        }
        if (image.maxPixelValue > maxValue) {
          maxValue = image.maxPixelValue;
        }
      }

      // If no images were loaded yet, return default range
      if (minValue === Infinity && maxValue === -Infinity) {
        return [0, 0];
      }

      return [minValue, maxValue];
    };

    // The length comes from the grid of the volume, and not from the image of
    // slice 0, because that image can hold fewer voxels than a slice: a
    // progressive loader delivers a reduced image first.
    voxelManager._getScalarDataLength = () =>
      resolveSliceVoxelManager(0)
        ? dimensions[0] * dimensions[1] * dimensions[2] * numberOfComponents
        : 0;

    /**
     * Retrieves the scalar data in a memory-inefficient manner.
     * Useful for debugging, testing, or methods requiring all scalar data at once.
     *
     * IMPORTANT: This is READ ONLY. Changes to the returned buffer are never
     * reflected in the underlying data unless individually merged back.
     *
     * @returns {ArrayLike<number>} The scalar data array (read-only)
     */
    voxelManager.getCompleteScalarDataArray = () => {
      const ScalarDataConstructor = voxelManager._getConstructor();
      if (!ScalarDataConstructor) {
        return new Uint8Array(0);
      }

      const dataLength = voxelManager.getScalarDataLength();
      const scalarData = new ScalarDataConstructor(dataLength);

      const sliceSize = dimensions[0] * dimensions[1] * numberOfComponents;

      for (let sliceIndex = 0; sliceIndex < dimensions[2]; sliceIndex++) {
        const imageVoxelManager = resolveSliceVoxelManager(sliceIndex);

        if (imageVoxelManager) {
          const sliceStart = sliceIndex * sliceSize;
          // `getSliceData` scales an image that holds a different number of
          // voxels from the slice, and it copies an image that matches.
          const pixelData = sliceScalings[sliceIndex]
            ? voxelManager.getSliceData({ sliceIndex, slicePlane: 2 })
            : imageVoxelManager.getScalarData();

          if (numberOfComponents === 1) {
            scalarData.set(pixelData, sliceStart);
          } else {
            // For RGB(A) data, we need to ensure correct component ordering
            for (let i = 0; i < pixelData.length; i += numberOfComponents) {
              for (let j = 0; j < numberOfComponents; j++) {
                scalarData[sliceStart + i + j] = pixelData[i + j];
              }
            }
          }
        }
      }

      return scalarData;
    };

    voxelManager.setCompleteScalarDataArray = (scalarData) => {
      const sliceSize = dimensions[0] * dimensions[1] * numberOfComponents;
      const SliceDataConstructor = voxelManager._getConstructor();

      // Track min/max values across all slices
      let minValue = Infinity;
      let maxValue = -Infinity;

      for (let sliceIndex = 0; sliceIndex < dimensions[2]; sliceIndex++) {
        const imageVoxelManager = resolveSliceVoxelManager(sliceIndex);

        if (imageVoxelManager && SliceDataConstructor) {
          const sliceStart = sliceIndex * sliceSize;
          const sliceEnd = sliceStart + sliceSize;
          // @ts-ignore
          const sliceData = new SliceDataConstructor(sliceSize);
          // @ts-ignore
          sliceData.set(scalarData.subarray(sliceStart, sliceEnd));

          // Instead of directly assigning scalarData, use TypedArray's set method
          // previously here we were using imageVoxelManager.scalarData = sliceData
          // which had some weird side effects
          if (
            imageVoxelManager.scalarData &&
            imageVoxelManager.scalarData.length >= sliceSize
          ) {
            imageVoxelManager.scalarData.set(sliceData);
            // Ensure the voxel manager knows about the changes
            imageVoxelManager.modifiedSlices.add(sliceIndex);
          } else {
            // The image holds its values another way, or it holds fewer voxels
            // than the slice. A write through this voxel manager maps each
            // index into the image, so it fits either case.
            for (let i = 0; i < sliceSize; i++) {
              voxelManager.setAtIndex(sliceStart + i, sliceData[i]);
            }
          }

          // Update min/max values for this slice
          for (let i = 0; i < sliceData.length; i++) {
            const value = sliceData[i];
            minValue = Math.min(minValue, value);
            maxValue = Math.max(maxValue, value);
          }

          // Update the image's min/max pixel values
          const imageId = imageIds[sliceIndex];
          const image = cache.getImage(imageId);
          if (image) {
            image.minPixelValue = minValue;
            image.maxPixelValue = maxValue;
          }
        }
      }

      // Mark all slices as modified
      for (let k = 0; k < dimensions[2]; k++) {
        voxelManager.modifiedSlices.add(k);
      }

      // Update bounds
      voxelManager.boundsIJK = [
        [0, dimensions[0] - 1],
        [0, dimensions[1] - 1],
        [0, dimensions[2] - 1],
      ];
    };

    // Every one of the four function-valued properties is installed above, so
    // this factory returns the VOLUME interface, and a caller of one of the
    // four needs no guard.
    //
    // The assertion goes through `unknown` for two reasons, and each one is a
    // limit of the compiler and not a doubt about the code. First, the class
    // declares the four properties as optional, because other factories install
    // none of them, so the compiler cannot see the four assignments above.
    // Second, the instance is a `VoxelManager<number | RGB>` and the result is
    // a union of two voxel managers, one over `number` and one over `RGB`.
    return voxelManager as unknown as
      | IVolumeVoxelManager<number>
      | IVolumeVoxelManager<RGB>;
  }

  /**
   *  Creates a volume value accessor, based on a volume scalar data instance.
   * This also works for image value accessors for single plane (k=0) accessors.
   *
   * This should be deprecated in favor of the createImageVolumeVoxelManager
   * method since that one does not need to know the number of scalar data and
   * it creates them on the fly.
   */
  public static createScalarVolumeVoxelManager({
    dimensions,
    scalarData,
    numberOfComponents,
    id,
  }: {
    dimensions: Point3;
    scalarData;
    numberOfComponents?: number;
    id?: string;
  }): IVoxelManager<number> | IVoxelManager<RGB> {
    if (dimensions.length !== 3) {
      throw new Error(
        'Dimensions must be provided as [number, number, number] for [width, height, depth]'
      );
    }

    if (!numberOfComponents) {
      numberOfComponents =
        scalarData.length / dimensions[0] / dimensions[1] / dimensions[2];
      // We only support 1,3,4 component data, and sometimes the scalar data
      // doesn't match for some reason, so throw an exception
      if (
        numberOfComponents > 4 ||
        numberOfComponents < 1 ||
        numberOfComponents === 2
      ) {
        throw new Error(
          `Number of components ${numberOfComponents} must be 1, 3 or 4`
        );
      }
    }
    if (numberOfComponents > 1) {
      return VoxelManager._createRGBScalarVolumeVoxelManager({
        dimensions,
        scalarData,
        numberOfComponents,
        id,
      });
    }
    return VoxelManager._createNumberVolumeVoxelManager({
      dimensions,
      scalarData,
      id,
    });
  }

  /**
   * Creates a voxel manager over a set of dimension groups, and gives the
   * values of the active dimension group.
   *
   * THE RESULT IS AN `IVoxelManager` AND NOT AN `IVolumeVoxelManager`. This
   * factory installs `getRange` and `getCompleteScalarDataArray`, and it
   * installs neither `setCompleteScalarDataArray` nor `invalidateCache`, so a
   * call to one of those two throws a TypeError. A caller must guard the call,
   * or somebody must add the two delegations to this factory in later work.
   */
  public static createScalarDynamicVolumeVoxelManager({
    imageIdGroups,
    dimensions,
    dimensionGroupNumber = 1,
    timePoint = 0,
    numberOfComponents = 1,
    id,
  }: {
    imageIdGroups: string[][];
    dimensions: Point3;
    dimensionGroupNumber?: number;
    timePoint?: number;
    numberOfComponents?: number;
    id?: string;
  }): IVoxelManager<number> | IVoxelManager<RGB> {
    // Convert to zero-based index for internal use
    let activeDimensionGroup = 0;

    if (dimensionGroupNumber !== undefined) {
      activeDimensionGroup = dimensionGroupNumber - 1;
    } else if (timePoint !== undefined) {
      log.warn(
        'Warning: timePoint parameter is deprecated. Please use dimensionGroupNumber instead. timePoint is zero-based while dimensionGroupNumber starts at 1.'
      );
      activeDimensionGroup = timePoint;
    }

    if (!numberOfComponents) {
      const firstImage = cache.getImage(imageIdGroups[0][0]);
      if (!firstImage) {
        throw new Error(
          'Unable to determine number of components: No image found'
        );
      }
      numberOfComponents =
        firstImage.getPixelData().length / (dimensions[0] * dimensions[1]);
      if (
        numberOfComponents > 4 ||
        numberOfComponents < 1 ||
        numberOfComponents === 2
      ) {
        throw new Error(
          `Number of components ${numberOfComponents} must be 1, 3 or 4`
        );
      }
    }

    const voxelGroups = imageIdGroups.map((imageIds) => {
      return VoxelManager.createImageVolumeVoxelManager({
        dimensions,
        imageIds,
        numberOfComponents,
        id,
      });
    });

    // Create a VoxelManager that will manage the active voxel group
    const voxelManager = new VoxelManager<number | RGB>(dimensions, {
      _get: (index) => voxelGroups[activeDimensionGroup]._get(index),
      // @ts-ignore
      _set: (index, v) => voxelGroups[activeDimensionGroup]._set(index, v),
      numberOfComponents,
      _id: id || 'createScalarDynamicVolumeVoxelManager',
    }) as IVoxelManager<number> | IVoxelManager<RGB>;

    voxelManager.getScalarDataLength = () => {
      return voxelGroups[activeDimensionGroup].getScalarDataLength();
    };

    voxelManager.getConstructor = () => {
      return voxelGroups[activeDimensionGroup].getConstructor();
    };

    voxelManager.getRange = () => {
      return voxelGroups[activeDimensionGroup].getRange();
    };

    voxelManager.getMiddleSliceData = () => {
      return voxelGroups[activeDimensionGroup].getMiddleSliceData();
    };

    // Each group remembers a slice that had no image, so an arrival must reach
    // every group and not only the active one.
    voxelManager.invalidateSlice = (sliceIndex: number) => {
      voxelGroups.forEach((group) => group.invalidateSlice?.(sliceIndex));
    };

    // @ts-ignore
    voxelManager.setTimePoint = (newTimePoint: number) => {
      log.warn(
        'Warning: setTimePoint is deprecated. Please use setDimensionGroupNumber instead. Note that timePoint is zero-based while dimensionGroupNumber starts at 1.'
      );
      // @ts-ignore
      voxelManager.setDimensionGroupNumber(newTimePoint + 1);
    };

    // @ts-ignore
    voxelManager.setDimensionGroupNumber = (
      newDimensionGroupNumber: number
    ) => {
      activeDimensionGroup = newDimensionGroupNumber - 1;
      // @ts-ignore
      voxelManager._get = (index) =>
        voxelGroups[activeDimensionGroup]._get(index);
      // @ts-ignore
      voxelManager._set = (index, v) =>
        voxelGroups[activeDimensionGroup]._set(index, v);
    };

    // @ts-ignore
    voxelManager.getAtIndexAndTimePoint = (index: number, tp: number) => {
      log.warn(
        'Warning: getAtIndexAndTimePoint is deprecated. Please use getAtIndexAndDimensionGroup instead. Note that timePoint is zero-based while dimensionGroupNumber starts at 1.'
      );
      // @ts-ignore
      return voxelManager.getAtIndexAndDimensionGroup(index, tp + 1);
    };

    // @ts-ignore
    voxelManager.getAtIndexAndDimensionGroup = (
      index: number,
      dimensionGroupNumber: number
    ) => {
      return voxelGroups[dimensionGroupNumber - 1]._get(index);
    };

    // @ts-ignore
    voxelManager.getTimePointScalarData = (tp: number) => {
      log.warn(
        'Warning: getTimePointScalarData is deprecated. Please use getDimensionGroupScalarData instead. Note that timePoint is zero-based while dimensionGroupNumber starts at 1.'
      );
      // @ts-ignore
      return voxelManager.getDimensionGroupScalarData(tp + 1);
    };

    // @ts-ignore
    voxelManager.getDimensionGroupScalarData = (
      dimensionGroupNumber: number
    ) => {
      return voxelGroups[dimensionGroupNumber - 1].getCompleteScalarDataArray();
    };

    // @ts-ignore
    voxelManager.getCurrentTimePointScalarData = () => {
      log.warn(
        'Warning: getCurrentTimePointScalarData is deprecated. Please use getCurrentDimensionGroupScalarData instead.'
      );
      // @ts-ignore
      return voxelManager.getCurrentDimensionGroupScalarData();
    };

    // @ts-ignore
    voxelManager.getCurrentDimensionGroupScalarData = () => {
      return voxelGroups[activeDimensionGroup].getCompleteScalarDataArray();
    };

    // Expose the generic scalar-data accessor so consumers that operate on a plain
    // volume voxel manager (e.g. segmentation statistics) work on dynamic volumes,
    // returning the currently active dimension group's scalar data.
    // @ts-ignore
    voxelManager.getCompleteScalarDataArray = () => {
      return voxelGroups[activeDimensionGroup].getCompleteScalarDataArray();
    };

    // @ts-ignore
    voxelManager.getCurrentTimePoint = () => {
      log.warn(
        'Warning: getCurrentTimePoint is deprecated. Please use getCurrentDimensionGroupNumber instead. Note that timePoint is zero-based while dimensionGroupNumber starts at 1.'
      );
      return activeDimensionGroup;
    };

    // @ts-ignore
    voxelManager.getCurrentDimensionGroupNumber = () => {
      return activeDimensionGroup + 1;
    };

    return voxelManager as IVoxelManager<number> | IVoxelManager<RGB>;
  }

  public static createImageVoxelManager({
    width,
    height,
    scalarData,
    numberOfComponents = 1,
    id,
  }: {
    width: number;
    height: number;
    scalarData: PixelDataTypedArray;
    numberOfComponents?: number;
    id?: string;
  }): IVoxelManager<number> | IVoxelManager<RGB> {
    const dimensions = [width, height, 1] as Point3;
    if (!numberOfComponents) {
      numberOfComponents = scalarData.length / width / height;
      if (
        numberOfComponents > 4 ||
        numberOfComponents < 1 ||
        numberOfComponents === 2
      ) {
        throw new Error(
          `Number of components ${numberOfComponents} must be 1, 3 or 4`
        );
      }
    }
    if (numberOfComponents > 1) {
      return VoxelManager._createRGBScalarVolumeVoxelManager({
        dimensions,
        scalarData,
        numberOfComponents,
        id,
      });
    }
    return VoxelManager._createNumberVolumeVoxelManager({
      dimensions,
      scalarData,
      id,
    });
  }

  /**
   * Creates a volume voxel manager that works on single numeric values stored
   * in an array like structure of numbers.
   */
  private static _createNumberVolumeVoxelManager({
    dimensions,
    scalarData,
    id,
  }: {
    dimensions: Point3;
    scalarData: PixelDataTypedArray;
    id?: string;
  }): IVoxelManager<number> {
    const voxels = new VoxelManager<number>(dimensions, {
      _get: (index) => scalarData[index],
      _set: (index, v) => {
        const isChanged = scalarData[index] !== v;
        scalarData[index] = v;
        return isChanged;
      },
      _getConstructor: () =>
        scalarData.constructor as new (length: number) => PixelDataTypedArray,
      _id: id || '_createNumberVolumeVoxelManager',
    });
    voxels.scalarData = scalarData;
    voxels.clear = () => {
      // get the latest scalar data and set that to all 0
      voxels.scalarData.fill(0);
    };

    voxels.getMiddleSliceData = () => {
      const middleSliceIndex = Math.floor(dimensions[2] / 2);
      return voxels.getSliceData({
        sliceIndex: middleSliceIndex,
        slicePlane: 2,
      });
    };

    return voxels;
  }

  /**
   * Creates a volume map value accessor.  This is initially empty and
   * the map stores the index to value instances.
   * This is useful for sparse matrices containing pixel data.
   */
  public static createMapVoxelManager<T>({
    dimension,
    id,
  }: {
    dimension: Point3;
    id?: string;
  }): IVoxelManager<T> {
    const map = new Map<number, T>();
    const voxelManager = new VoxelManager<T>(dimension, {
      _get: map.get.bind(map),
      _set: (index, v) => map.set(index, v) && true,
      _id: id || 'createMapVoxelManager',
    });
    voxelManager.map = map;
    return voxelManager;
  }

  /**
   * Creates a history remembering voxel manager.
   * This will remember the original values in the voxels, and will apply the
   * update to the underlying source voxel manager.
   */
  public static createHistoryVoxelManager<T>(
    sourceVoxelManager: IVoxelManager<T>,
    id?: string
  ): IVoxelManager<T> {
    const map = new Map<number, T>();
    const { dimensions } = sourceVoxelManager;
    const voxelManager = new VoxelManager(dimensions, {
      _get: (index) => map.get(index),
      _set: function (index, v) {
        if (!map.has(index)) {
          const oldV = this.sourceVoxelManager.getAtIndex(index);
          if (oldV === v) {
            // No-op
            return false;
          }
          map.set(index, oldV);
        } else if (v === map.get(index)) {
          map.delete(index);
        }
        this.sourceVoxelManager.setAtIndex(index, v);
      },
      _id: id || 'createHistoryVoxelManager',
    });
    voxelManager.map = map;
    // The cast reaches the private backing store of the source. Every voxel
    // manager is an instance of this class, and the assignment must copy the
    // field itself: `getWritableScalarData()` hides a cached expansion, and
    // this code copied a cached expansion before.
    voxelManager.scalarData = (
      sourceVoxelManager as VoxelManager<T>
    ).scalarData;
    voxelManager.sourceVoxelManager = sourceVoxelManager;
    return voxelManager;
  }

  /**
   * Creates a history remembering voxel manager, based on the RLE endpoint
   * rather than a map endpoint.
   * This will remember the original values in the voxels, and will apply the
   * update to the underlying source voxel manager.
   */
  public static createRLEHistoryVoxelManager<T>(
    sourceVoxelManager: IVoxelManager<T>,
    id?: string
  ): IVoxelManager<T> {
    const { dimensions } = sourceVoxelManager;
    const map = new RLEVoxelMap<T>(dimensions[0], dimensions[1], dimensions[2]);
    const voxelManager = new VoxelManager<T>(dimensions, {
      _get: (index) => map.get(index),
      _set: function (index, v) {
        const originalV = map.get(index);
        if (originalV === undefined) {
          const oldV = this.sourceVoxelManager.getAtIndex(index);
          if (oldV === v || (oldV === undefined && v === 0) || v === null) {
            // No-op
            return false;
          }
          map.set(index, oldV ?? 0);
        } else if (v === originalV || v === null) {
          map.delete(index);
          v = originalV;
        }
        this.sourceVoxelManager.setAtIndex(index, v);
      },
      _getScalarData: RLEVoxelMap.getScalarData,
      _updateScalarData: (scalarData) => {
        map.updateScalarData(scalarData as PixelDataTypedArray);
        return scalarData as PixelDataTypedArray;
      },
      _id: id || 'createRLEHistoryVoxelManager',
    });
    voxelManager.map = map;
    voxelManager.sourceVoxelManager = sourceVoxelManager;
    return voxelManager;
  }

  /**
   * Creates a lazy voxel manager that will create an image plane as required
   * for each slice of a volume as it gets changed.  This can be used to
   * store image data that gets created as required.
   */
  public static createLazyVoxelManager<T>({
    dimensions,
    planeFactory,
    id,
  }: {
    dimensions: Point3;
    planeFactory: (width: number, height: number) => T;
    id?: string;
  }): IVoxelManager<T> {
    const map = new Map<number, T>();
    const [width, height] = dimensions;
    const planeSize = width * height;

    const voxelManager = new VoxelManager(dimensions, {
      _get: (index) =>
        map.get(Math.floor(index / planeSize))[index % planeSize],
      _set: (index, v) => {
        const k = Math.floor(index / planeSize);
        let layer = map.get(k);
        if (!layer) {
          layer = planeFactory(width, height);
          map.set(k, layer);
        }
        layer[index % planeSize] = v;
        return true;
      },
      _id: id || 'createLazyVoxelManager',
    });
    voxelManager.map = map;
    return voxelManager;
  }

  /**
   * Creates a RLE based voxel manager.  This is effective for storing
   * segmentation maps or already RLE encoded data such as ultrasounds.
   *
   * `pixelDataConstructor` and `defaultValue` let a caller that is using the
   * RLE map to STAND IN FOR a typed array (a labelmap image, say) match what
   * that array would have done: expanded frames come back as the same type, and
   * an unwritten voxel reads as the array's zero rather than undefined.
   */
  public static createRLEVolumeVoxelManager<T>({
    dimensions,
    id,
    pixelDataConstructor,
    defaultValue,
  }: {
    dimensions: Point3;
    id?: string;
    pixelDataConstructor?: new (length: number) => PixelDataTypedArray;
    defaultValue?: T;
  }): IVoxelManager<T> {
    const [width, height, depth] = dimensions;
    const map = new RLEVoxelMap<T>(width, height, depth);

    if (pixelDataConstructor) {
      map.pixelDataConstructor =
        pixelDataConstructor as typeof map.pixelDataConstructor;
    }

    if (defaultValue !== undefined) {
      map.defaultValue = defaultValue;
    }

    const voxelManager = new VoxelManager<T>(dimensions, {
      _get: (index) => map.get(index),
      _set: (index, v) => {
        map.set(index, v);
        return true;
      },
      _getScalarData: RLEVoxelMap.getScalarData,
      // Only where the caller chose a type. Without this, getConstructor() has
      // no scalar data to read the type from and falls back to Float32Array,
      // which the stack labelmap actor would then build its texture as; but a
      // caller that chose nothing is left on that pre-existing fallback rather
      // than being moved onto a type it never asked for.
      _getConstructor: pixelDataConstructor
        ? () =>
            map.pixelDataConstructor as new (
              length: number
            ) => PixelDataTypedArray
        : undefined,
      _updateScalarData: (scalarData) => {
        map.updateScalarData(scalarData as PixelDataTypedArray);
        return scalarData as PixelDataTypedArray;
      },
      _id: id || 'createRLEVolumeVoxelManager',
    });
    voxelManager.map = map;
    // @ts-ignore
    voxelManager.getPixelData = map.getPixelData.bind(map);
    // @ts-ignore
    return voxelManager;
  }

  public static createRLEImageVoxelManager<T>({
    dimensions,
    id,
    pixelDataConstructor,
    defaultValue,
  }: {
    dimensions: Point2;
    id?: string;
    pixelDataConstructor?: new (length: number) => PixelDataTypedArray;
    defaultValue?: T;
  }): IVoxelManager<T> {
    const [width, height] = dimensions;
    return VoxelManager.createRLEVolumeVoxelManager<T>({
      dimensions: [width, height, 1],
      id,
      pixelDataConstructor,
      defaultValue,
    });
  }

  /**
   * This method adds a voxelManager instance to the image object
   * where the object added is of type:
   * 1. RLE map if the scalar data is missing or too small (dummy data)
   * 2. Volume VoxelManager scalar data representations
   */
  public static addInstanceToImage(image: IImage) {
    const { width, height } = image;
    const scalarData = image.voxelManager.getScalarData();
    // This test works for single images, or single representations of images
    // from a volume representation, for grayscale, indexed and RGB or RGBA images.
    if (scalarData.length >= width * height) {
      // This case means there is enough scalar data for at least one image,
      // with 1 or more components, and creates a volume voxel manager
      // that can lookup the data
      image.voxelManager = VoxelManager.createScalarVolumeVoxelManager({
        dimensions: [width, height, 1],
        scalarData,
      });
      return;
    }
    // This case occurs when the image data is a dummy image data set
    // created just to prevent exceptions in the caching logic.  Then, the
    // RLE voxel manager can be created to store the data instead.
    image.voxelManager = VoxelManager.createRLEVolumeVoxelManager<number>({
      dimensions: [width, height, 1],
    });
    // The RLE voxel manager knows how to get scalar data pixel data representations.
    // That allows using the RLE representation as a normal pixel data representation
    // for VIEWING purposes.
    // @ts-ignore
    image.getPixelData = image.voxelManager.getPixelData;
    // Assign a different size to the cached data because this is actually
    // storing an RLE representation, which doesn't have an up front size.
    image.sizeInBytes = DEFAULT_RLE_SIZE;
  }
}

export type { VoxelManager };
